import React, { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import useDidUpdate from '../../../../../../hooks/useDidUpdate.effect';
import { documentService } from '../../../../../../utils/window.utils.js';
import Icon from '../../../../../@shared/icon';
import HiglightedOption from '../../../../../@shared/highlightedOption';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import { TCountryStateFieldDropdown } from '../../../../types/countryStateField.types';
import '../../styles.scss';

const CountryStateDropDown: FC<TCountryStateFieldDropdown> = ({
  dropdownOptions = [],
  id,
  handleOptionClick,
  selectedOptionInd,
  fields,
  label,
  isEnable,
  isCountryDropDown,
}) => {
  const { field, tracker } = fields;
  const hasAnyError = !!fields?.errors?.length;
  const [isVisible, setIsVisible] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasTextInputFocus, setHasTextInputFocus] = useState(false);
  const [initialTextValue, setInitialTextValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [onSearch, setOnSearch] = useState(false);
  const dropdownRef: React.MutableRefObject<HTMLUListElement | null> = useRef(null);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const filteredItems = dropdownOptions?.filter((item) => {
    const itemSelected = isCountryDropDown ? item?.country : item?.state;
    return itemSelected?.toLocaleLowerCase().includes(textValue?.toLocaleLowerCase());
  });
  const visibleDropdownOptions = initialTextValue !== textValue && textValue !== '' ? filteredItems : dropdownOptions;

  useEffect(() => {
    if (!fields.value) {
      setTextValue('');
      setInitialTextValue('');
      setOnSearch(false);
    }
  }, [fields.value]);

  const onDropdownFocus = (): void => {
    setHasFocus(true);
    setHasTextInputFocus(true);
  };

  const onTextInputBlur = (): void => {
    setHasTextInputFocus(false);
    setOnSearch(false);
  };

  const onDropdownClik = (): void => {
    setIsVisible(!isVisible);

    if (!isVisible) {
      inputRef?.current?.focus();
    } else {
      inputRef?.current?.blur();
    }
  };

  const onSelectOption = (value, text): void => {
    setTextValue(text);
    setInitialTextValue(value);
    setIsVisible(false);
  };

  const handleDropdownSelectOption = (index: number, itemId?: string) => {
    if (visibleDropdownOptions?.length === dropdownOptions.length) {
      handleOptionClick(index);
    } else {
      const findIndex = dropdownOptions?.findIndex((item) => item.id === itemId);
      handleOptionClick(findIndex);
    }
  };

  const onInputUpdate = (e: ChangeEvent<HTMLInputElement>): void => {
    setTextValue(e?.target?.value);
    setOnSearch(true);
  };

  const handleClickOutside = (event): void => {
    if (dropdownRef?.current?.contains(event?.target)) {
      return;
    }

    setIsVisible(false);

    if (textValue === '') {
      setTextValue('');
    } else {
      const hasMatch = dropdownOptions.some((opt) => (isCountryDropDown ? opt.country : opt.state) === textValue);

      if (!hasMatch) {
        setTextValue(initialTextValue);
      }
    }

    setHasFocus(false);
  };

  useEffect(() => {
    documentService()?.addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService()?.removeEventListener('mouseup', handleClickOutside);
    };
  }, [initialTextValue, textValue]); // eslint-disable-line

  useDidUpdate(() => {
    if (hasFocus) {
      tracker?.onFocusField(field, textValue);
    } else {
      tracker?.onBlurField(field, textValue);
    }
  }, [hasFocus]);

  return (
    <>
      <ul className={`form-custom-dropdown ${hasAnyError ? 'form-custom-dropdown--has-error' : ''}`} ref={dropdownRef}>
        <li
          className={`form-custom-dropdown__input-container ${hasFocus ? 'form-custom-dropdown__input-container--has-focus' : ''} ${
            !isEnable ? 'form-custom-dropdown__input-container--disable' : ''
          }`}
        >
          <label
            htmlFor={id}
            className={`form-custom-dropdown__label ${!isEnable ? 'form-custom-dropdown__label--disable' : ''} ${
              (!hasTextInputFocus && textValue === '' && !hasFocus && selectedOptionInd === '') || (!hasFocus && selectedOptionInd === '')
                ? 'form-custom-dropdown__label--empty-field'
                : ''
            }`}
          >
            <DictionaryTerm keyword={label} />
          </label>
          <input
            className={`form-custom-dropdown__text-input ${!isEnable ? 'form-custom-dropdown__text-input--disable' : ''}`}
            type="text"
            value={onSearch ? textValue : selectedOptionInd || ''}
            onChange={onInputUpdate}
            ref={inputRef}
            id={id}
            onClick={onDropdownClik}
            onFocus={onDropdownFocus}
            onBlur={onTextInputBlur}
            autoComplete="off"
            form="novalidatedform"
          />
          <Icon
            iconName={isVisible ? 'arrow-chevron-up' : 'arrow-chevron-down'}
            size={24}
            extraClassName="form-custom-dropdown__icon"
            color="grey"
            onClick={onDropdownClik}
          />
        </li>

        <li className={`form-custom-dropdown__list-container ${isVisible ? 'form-custom-dropdown__list-container--visible' : ''}`}>
          <ul className="form-custom-dropdown__list" role="listbox">
            <>
              {visibleDropdownOptions.map((item, index) => {
                const itemSelected = isCountryDropDown ? item?.country : item?.state;
                return (
                  <li
                    className="form-custom-dropdown__list-item"
                    role="option" // eslint-disable-line
                    onClick={(): void => {
                      onSelectOption(itemSelected || '', itemSelected || '');
                      handleDropdownSelectOption(index, item?.id);
                    }}
                    key={item.id}
                  >
                    <HiglightedOption keyword={textValue} optionText={itemSelected} key={item.id} />
                  </li>
                );
              })}
            </>
          </ul>
        </li>
      </ul>
    </>
  );
};

export default CountryStateDropDown;
